//utils/apiFunction.ts
import axios from "axios";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const cookies = new Cookies();

interface ApiHeaders {
  apiKey?: string;
  userId?: string;
  token?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  userId: string;
  token: string;
}

interface UserAuth {
  userId?: string;
  token?: string;
}

const getAxiosHeaders = (headers: ApiHeaders) => ({
  "Content-Type": "application/json",
  ...headers,
});

export const getUserAuth = async ({
  BACKEND_URL,
  apiKey,
  entityId,
  uniqueUserId,
  uniqueEmailId,
  userId,
  token,
  GeneralFunctions,
  entityAuthenticationToken,
}: {
  BACKEND_URL: string;
  apiKey: string;
  entityId: string;
  uniqueUserId: string;
  uniqueEmailId: string;
  userId: string;
  token: string;
  GeneralFunctions: any;
  entityAuthenticationToken?: string;
}): Promise<UserAuth | null> => {
  try {
    const externalUserId = cookies.get("externalUserId");
    const questUserId = cookies.get("questUserId");
    const questUserToken = cookies.get("questUserToken");

    if (
      externalUserId &&
      questUserId &&
      questUserToken &&
      (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
    ) {
      return {
        userId: questUserId,
        token: questUserToken,
      };
    }

    if (uniqueUserId) {
      const headers = getAxiosHeaders({
        apiKey,
        ...(token && { token }),
        ...(userId && { userId }),
        ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
      });

      const body = {
        externalUserId: uniqueUserId,
        entityId,
        email: uniqueEmailId || null,
        ignoreMAU: true,
      };

      const response = await axios.post<ApiResponse<UserAuth>>(
        `${BACKEND_URL}api/users/external/login`,
        body,
        { headers }
      );

      if (!response.data?.success) {
        throw new Error("Invalid response from external login");
      }
      const { userId: newUserId, token: newToken } = response.data;
      const expires = new Date();
      expires.setHours(expires.getHours() + 12);

      cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
      cookies.set("questUserId", newUserId, { path: "/", expires });
      cookies.set("questUserToken", newToken, { path: "/", expires });

      return {
        userId: newUserId,
        token: newToken,
      };
    }

    if (userId && token) {
      return { userId, token };
    }

    return null;
  } catch (error) {
    GeneralFunctions.captureSentryException(error);
    return null;
  }
};

export const fetchData = async ({
  BACKEND_URL,
  entityId,
  campaignId,
  variation,
  apiKey,
  apiSecret,
  userId,
  token,
  apiType,
  setQuestData,
  setUiProps,
  setBrandTheme,
  setQuestThemeData,
  setFormdata,
  // setData,
  bgColor,
  uniqueUserId,
  uniqueEmailId,
  entityAuthenticationToken,
  GeneralFunctions,
  headings,
  setHeadings,
  setShowFooter,
  setDesignApi,
  setShowLoader,
}: // setGradient,
{
  BACKEND_URL: string;
  entityId: string;
  campaignId: string;
  variation?: string;
  apiKey: string;
  apiSecret: string;
  userId: string;
  token: string;
  apiType: string;
  setQuestData: Function;
  setUiProps: Function;
  setBrandTheme: Function;
  setQuestThemeData: Function;
  setFormdata: Function;
  // setData: Function;
  bgColor?: string;
  uniqueUserId: string;
  uniqueEmailId: string;
  entityAuthenticationToken?: string;
  GeneralFunctions: any;
  headings: any;
  setHeadings: Function;
  setShowFooter: Function;
  // setGradient: Function;
  setDesignApi: Function;
  setShowLoader: Function;
}) => {
  try {
    GeneralFunctions.fireTrackingEvent("quest_survey_loaded", "survey");
    setShowLoader(true);
    // if (bgColor) {
    //   setGradient(
    //     bgColor.includes("linear-gradient") ||
    //       bgColor.includes("radial-gradient")
    //   );
    // }

    const userAuth = await getUserAuth({
      BACKEND_URL,
      apiKey,
      entityId,
      uniqueUserId,
      uniqueEmailId,
      userId,
      token,
      entityAuthenticationToken,
      GeneralFunctions,
    });

    if (!userAuth) {
      toast.error("Authentication failed");
      setShowLoader(false);
      return;
    }

    const params = new URLSearchParams();
    params.set("platform", "REACT");
    if (variation) params.set("variation", variation);

    const headers = {
      apiKey,
      apisecret: apiSecret,
      userId: userAuth.userId,
      token: userAuth.token,
    };

    const response = await axios.get<ApiResponse<any>>(
      `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`,
      { headers }
    );

    if (!response.data?.success) {
      throw new Error("Failed to fetch campaign data");
    }
    setQuestData(response.data.data);
    setUiProps(response.data.data?.sdkConfig?.uiProps);
    if (!headings || headings?.length === 0) {
      setHeadings(response.data.data?.sdkConfig?.uiProps?.Headers);
    }
    
    if (response.data.data.sdkConfig?.uiProps?.questThemeData) {
      setQuestThemeData(response.data.data.sdkConfig.uiProps.questThemeData);
    }
    
    if (response.data.data.sdkConfig?.uiProps?.BrandTheme?.properties) {
      setBrandTheme(response.data.data.sdkConfig.uiProps.BrandTheme?.properties);
    }

    if (response.data.data.sdkConfig?.uiProps?.showFooter !== undefined) {
      setShowFooter(response.data.data.sdkConfig.uiProps.showFooter);
    }
    
    if (response.data.data.sdkConfig?.uiProps?.design) {
      setDesignApi(response.data.data.sdkConfig.uiProps.design);
    }
    
    const actions =
    response.data.data?.actions?.map((action) => ({
      type: action?.actionType,
      question: action?.title,
      options: action?.options || [],
      actionId: action?.actionId,
      required: action?.isRequired,
      placeholder: action?.metadata?.placeholder,
      ...(action?.metadata?.regexKey ? { regexKey: action?.metadata?.regexKey } : null),
      ...(action?.metadata?.regexErrorMessage ? { regexMessage : action?.metadata?.regexErrorMessage } : null),
      ...(action?.metadata?.leftRatingText ? { leftRatingText: action?.metadata?.leftRatingText } : null),
      ...(action?.metadata?.rightRatingText ? { rightRatingText: action?.metadata?.rightRatingText } : null),
      ...(action?.metadata?.centerRatingText ? { centerRatingText: action?.metadata?.centerRatingText } : null),
      ...(action?.metadata?.ratingType ? { ratingType: action?.metadata?.ratingType } : null),
      
      
    })) || [];
    
    setFormdata([...actions]);
    setShowLoader(false);
    // setData([...actions]);
  } catch (error) {
    GeneralFunctions.captureSentryException(error);
    toast.error("An error occurred while fetching data.");
  } finally {
    setShowLoader(false);
  }
};

export const returnAnswers = async ({
  BACKEND_URL,
  entityId,
  campaignId,
  formdata,
  answer,
  selectedOptions,
  questData,
  // setShowLoader,
  setThanksPopup,
  onSubmit,
  GeneralFunctions,
  apiKey,
  apiSecret,
  userId,
  token,
  uniqueUserId,
  toast,
}: {
  BACKEND_URL: string;
  entityId: string;
  campaignId: string;
  formdata: any;
  answer: any;
  selectedOptions: any;
  questData: any;
  // setShowLoader: any;
  setThanksPopup: any;
  onSubmit: any;
  GeneralFunctions: any;
  apiKey: string;
  apiSecret: string;
  userId: string;
  token: string;
  uniqueUserId: string;
  toast: any;
}) => {
  GeneralFunctions.fireTrackingEvent("quest_survey_form_submitted", "survey");
  GeneralFunctions.fireTrackingEvent(`quest_survey-${entityId}`, "survey");
  let callApi = true;
  const questUserId = cookies.get("questUserId");
  const questUserToken = cookies.get("questUserToken");
  const externalUserId = cookies.get("externalUserId");

  const headers = {
    apikey: apiKey,
    apisecret: apiSecret,
    userId:
      !!externalUserId &&
      !!questUserId &&
      !!questUserToken &&
      externalUserId == uniqueUserId
        ? questUserId
        : userId,
    token:
      !!externalUserId &&
      !!questUserId &&
      !!questUserToken &&
      externalUserId == uniqueUserId
        ? questUserToken
        : token,
  };

  if (callApi) {
    const ansArr = formdata.map((ans) => ({
      answers: [answer[ans?.actionId] || ""].map((ans) => String(ans)),
      actionId: ans?.actionId || "",
    }));

    const updatedAnsArr = ansArr.map((ans) => {
      if (selectedOptions[ans.actionId] !== undefined) {
        return {
          ...ans,
          answers: selectedOptions[ans.actionId],
        };
      }
      return ans;
    });

    const requestData = {
      actions: updatedAnsArr,
      campaignVariationId: questData.campaignVariationId,
    };
    // setShowLoader(true);
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}/verify`,
        requestData,
        { headers }
      );

      if (response.data.success) {
        setThanksPopup(true);
        onSubmit && onSubmit();
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error("Error submitting survey answers");
    }
    // finally {
    //   setShowLoader(false);
    // }
  }
};
